import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import Group from '../types/project/360/Group';
import type { ParticipantProgressItem } from '../types/project/statistics/ParticipantProgress';
import type {
    ProjectStatisticsById,
    ProjectStatisticsId,
} from '../types/project/statistics/ProjectStatistics';
import SortParams from '../types/SortParams';

const BASE_URL = '/admin/products';

const createStatisticsUrl = (
    productType: number,
    projectId: number,
    statisticsId: ProjectStatisticsId,
) =>
    `${BASE_URL}/${productType}/projects/${projectId}/statistics/${statisticsId}`;

const createParticipantsProgressUrl = (
    productType: number,
    projectId: number,
) => `${BASE_URL}/${productType}/projects/${projectId}/participants/progress`;

const create360GroupsUrl = (projectId: number) =>
    `${BASE_URL}/360/projects/${projectId}/groups`;

const AdminProjectStatisticsService = {
    getStatistics,
    getParticipantsProgress,
    get360Groups,
};

export interface GetStatisticsPayload<T extends ProjectStatisticsId> {
    productType: number;
    projectId: number;
    statisticsId: T;
}

async function getStatistics<T extends ProjectStatisticsId>(
    payload: GetStatisticsPayload<T>,
): Promise<RequestServiceResponse<ProjectStatisticsById[T]>> {
    const { productType, projectId, statisticsId } = payload;
    return await RequestService.get<ProjectStatisticsById[T]>(
        createStatisticsUrl(productType, projectId, statisticsId),
    );
}

export interface GetParticipantsProgressPayload {
    productType: number;
    projectId: number;
    limit?: number;
    page?: number;
    search?: string;
}

async function getParticipantsProgress(
    payload: GetParticipantsProgressPayload & SortParams,
): Promise<RequestServiceResponse<ParticipantProgressItem[]>> {
    const {
        productType,
        projectId,
        limit = 10,
        page = 1,
        sortBy,
        sortOrder,
        search,
    } = payload;

    return await RequestService.get<ParticipantProgressItem[]>(
        createParticipantsProgressUrl(productType, projectId),
        { limit, page, sortBy, sortOrder, search },
    );
}

export interface Get360GroupsPayload {
    projectId: number;
}

async function get360Groups(
    payload: Get360GroupsPayload,
): Promise<RequestServiceResponse<Group[]>> {
    const { projectId } = payload;
    return await RequestService.get<Group[]>(create360GroupsUrl(projectId));
}

export default AdminProjectStatisticsService;
